import { useCallback, useEffect, useState } from "react";
import { Laptop, LogOut, RefreshCw, ShieldCheck, Smartphone } from "lucide-react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useConfirm } from "@/context/ConfirmContext";
import { formatDateTime } from "@/lib/dateTime";
import { PageHeader } from "@/components/patterns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TwoFactorCard } from "@/components/settings/TwoFactorCard";

interface DeviceSession {
  id: string;
  device_label: string;
  ip: string | null;
  signed_in_at: string;
  last_seen_at: string;
  current: boolean;
}

const isPhone = (label: string) => /Android|iOS/.test(label);

/** Settings → Security: where this person is signed in, and signing a device out */
export function MySecurityPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { success, error } = useToast();
  const confirm = useConfirm();
  const [sessions, setSessions] = useState<DeviceSession[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setSessions(unwrapArray<DeviceSession>(await api.get("/api/auth/sessions")));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const others = (sessions ?? []).filter((s) => !s.current);

  const signOut = async (s: DeviceSession) => {
    if (busy) return;
    setBusy(s.id);
    try {
      await api.delete(`/api/auth/sessions/${s.id}`);
      success("Signed out", `${s.device_label} was signed out`);
    } catch (err: any) {
      // Already gone (signed out meanwhile): the refreshed list shows the truth
      if (err?.status !== 404) error("Could not sign out", err?.message || "Please try again");
    } finally {
      setBusy(null);
      load();
    }
  };

  const signOutOthers = async () => {
    if (busy) return;
    const ok = await confirm({
      title: "Sign out all other devices?",
      message: "Every other browser and phone signed in to your account will be signed out straight away. This device stays signed in.",
      confirmLabel: "Sign them out",
      destructive: true,
    });
    if (!ok) return;
    setBusy("others");
    try {
      const res: any = await api.post("/api/auth/sessions/revoke-others", {});
      success("Signed out", res?.Message || "Your other devices were signed out");
    } catch (err: any) {
      error("Could not sign out", err?.message || "Please try again");
    } finally {
      setBusy(null);
      load();
    }
  };

  return (
    <div className={embedded ? "space-y-4" : "mx-auto max-w-3xl space-y-4"}>
      {!embedded && <PageHeader title="Security" />}
      <TwoFactorCard />
      <section aria-labelledby="devices-heading" className="space-y-3 rounded-[10px] border border-border bg-card px-4 py-3.5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 id="devices-heading" className="text-sm font-semibold text-foreground">Where you’re signed in</h2>
            <p className="text-xs text-muted-foreground">
              Sign out any device you don’t recognise. It stops working straight away. We also email you when your account is signed in from a new kind of device.
            </p>
          </div>
          {others.length > 0 && (
            <Button variant="outline" size="sm" onClick={signOutOthers} disabled={busy !== null}>
              <LogOut className="mr-1.5 size-3.5" />
              {busy === "others" ? "Signing out…" : "Sign out all other devices"}
            </Button>
          )}
        </div>

        {sessions === null && !failed && (
          <div role="status" className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <RefreshCw className="size-4 animate-spin" aria-hidden="true" />
            Loading your devices…
          </div>
        )}
        {failed && (
          <p role="alert" className="py-2 text-sm text-danger">
            Could not load your devices. <button type="button" onClick={load} className="font-medium underline">Try again</button>
          </p>
        )}
        {sessions && sessions.length === 0 && (
          <p className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <ShieldCheck className="size-4 shrink-0" aria-hidden="true" />
            Your devices will be listed here from your next sign-in.
          </p>
        )}
        {sessions && sessions.length > 0 && (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {sessions.map((s) => {
              const Icon = isPhone(s.device_label) ? Smartphone : Laptop;
              return (
                <li key={s.id} className="flex flex-wrap items-center gap-3 px-3 py-3">
                  <span aria-hidden="true" className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <Icon className="size-[18px]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                      {s.device_label}
                      {s.current && <Badge variant="blue" className="text-[10px] font-normal">This device</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {s.ip ? `${s.ip} · ` : ""}Signed in {formatDateTime(s.signed_in_at)} · Last active {formatDateTime(s.last_seen_at)}
                    </p>
                  </div>
                  {!s.current && (
                    <Button variant="outline" size="sm" onClick={() => signOut(s)} disabled={busy !== null} aria-label={`Sign out ${s.device_label}, ${s.ip ?? "unknown address"}`}>
                      {busy === s.id ? "Signing out…" : "Sign out"}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
